import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { periodKpisQueryOptions } from "@/api/periodData";
import { matchKpisQueryOptions } from "@/api/ranking";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTeam } from "@/context/teamContext";
import { Loader } from "@/components/general/common/loader";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import { PeriodComparisonCard } from "@/components/general/Dashboard/PeriodData/PeriodComparisonCard";
import { PeriodSummaryTable } from "@/components/general/Dashboard/PeriodData/PeriodSummaryTable";
import { ONBOARDING_IDS } from "@/components/general/Onboarding/constants";
import { usePeriodData } from "../page";

export const PeriodDataPage = () => {
    const { matchIds } = useSelectedMatch();
    const { teamId } = useSelectedTeam();
    const { selectedPeriod, setSelectedPeriod, setPeriodNumbers } = usePeriodData();

    const { data, isLoading, isError } = useQuery(periodKpisQueryOptions(matchIds, teamId));

    const { data: kpiData } = useQuery(matchKpisQueryOptions(matchIds.length > 0 ? matchIds : [], teamId));

    const matchInfo = kpiData?.matches?.[0];
    const homeTeamName = matchInfo?.homeTeam;
    const awayTeamName = matchInfo?.awayTeam;
    const isChosenHome = matchInfo ? matchInfo.homeTeamId === teamId : true;

    const chosenPeriods = useMemo(() => data?.chosenPeriodKpis ?? [], [data]);
    const enemyPeriods = useMemo(() => data?.enemyTeamPeriodKpis ?? [], [data]);

    const allPeriodNumbers = useMemo(
        () => Array.from(new Set([...chosenPeriods, ...enemyPeriods].map((p) => p.period))).sort((a, b) => a - b),
        [chosenPeriods, enemyPeriods]
    );

    const homePeriods = isChosenHome ? chosenPeriods : enemyPeriods;
    const awayPeriods = isChosenHome ? enemyPeriods : chosenPeriods;

    useEffect(() => {
        if (allPeriodNumbers.length > 0) {
            setPeriodNumbers(allPeriodNumbers);
        }
        return () => {
            setPeriodNumbers([]);
            setSelectedPeriod("all");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPeriodNumbers.join(",")]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader message="Loading period data..." />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-on-surface-variant">
                <MaterialIcon name="error_outline" className="text-5xl opacity-30" />
                <p className="text-sm font-bold uppercase tracking-widest">Failed to load period data</p>
            </div>
        );
    }

    if (!data || allPeriodNumbers.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-on-surface-variant">
                <MaterialIcon name="hourglass_empty" className="text-5xl opacity-30" />
                <p className="text-sm font-bold uppercase tracking-widest">No period data available for this match</p>
            </div>
        );
    }

    return (
        <div id={ONBOARDING_IDS.PERIOD_DATA_CONTENT} className="space-y-4">
            {selectedPeriod === "all" ? (
                <PeriodSummaryTable
                    periods={allPeriodNumbers}
                    homeTeamPeriods={homePeriods}
                    awayTeamPeriods={awayPeriods}
                    homeTeamName={homeTeamName}
                    awayTeamName={awayTeamName}
                />
            ) : (
                <PeriodComparisonCard
                    period={selectedPeriod}
                    homePeriod={homePeriods.find((p) => p.period === selectedPeriod)}
                    awayPeriod={awayPeriods.find((p) => p.period === selectedPeriod)}
                    homeTeamName={homeTeamName}
                    awayTeamName={awayTeamName}
                />
            )}
        </div>
    );
};
